import React, { useState, useRef, useContext, useEffect } from 'react'
import styles from './index.module.css'
import { cn } from '@s4l/utility-css'
import { FormContext } from '../context'
import type { Rule } from '../Validations'
import Typography from '../../Typography'
import useInputValue from '../hooks/useInputValue'

type InputValue = string | number | undefined
interface IInputProps {
  name: string
  type?: 'text' | 'email' | 'password' | 'number' | 'tel'
  value?: string | number
  defaultValue?: string | number
  onChange?: (value: InputValue) => void
  onFocus?: () => void
  onBlur?: (value: string | number) => void
  disabled?: boolean
  placeholder?: string
  validations?: Array<ReturnType<Rule>>
  min?: number
  max?: number
  falseValue?: string | number
  displayValue?: (value: InputValue) => InputValue
  parseValue?: (value: InputValue) => InputValue
  onClear?: () => void
  startIcon?: React.ReactNode
}

const Input = ({
  type = 'text',
  value: controlledValue,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  disabled,
  name,
  validations,
  placeholder,
  min,
  max,
  falseValue = '',
  displayValue = value => value,
  parseValue = value => value,
  onClear,
  startIcon,
}: IInputProps) => {
  const [isFocused, setIsFocused] = useState(false)
  const [internalValue, setInternalValue] = useState(defaultValue)
  const inputRef = useRef<HTMLInputElement>(null)

  const { errors } = useContext(FormContext)

  const isControlled = controlledValue !== undefined
  const value = isControlled ? controlledValue : internalValue

  const { onInputChange } = useInputValue({
    name,
    defaultValue: value,
    falseValue,
    validations,
    setInputValue: newValue => {
      if (!isControlled && (typeof newValue === 'string' || typeof newValue === 'number')) {
        setInternalValue(newValue)
      }
    },
    onClear,
  })

  const handleFocus = () => {
    setIsFocused(true)
    onFocus?.()
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue: number | string = e.target.value

    const parsedValue = parseValue(newValue)
    onInputChange(parsedValue)

    if (!isControlled) {
      setInternalValue(parsedValue)
    }

    onChange?.(parsedValue)
  }

  const handleBlur = () => {
    setIsFocused(false)

    let newValue: string | number = value ?? falseValue

    if (type === 'number' || type === 'tel') {
      const numValue = Number(newValue)

      if (
        Number.isNaN(numValue) ||
        (min !== undefined && numValue < min) ||
        (max !== undefined && numValue > max)
      ) {
        newValue = falseValue
      } else {
        newValue = numValue
      }
    }

    onInputChange(newValue)

    if (!isControlled) {
      setInternalValue(newValue)
    }

    onChange?.(newValue)
    onBlur?.(newValue)
  }

  useEffect(() => {
    if (isControlled) {
      onInputChange(value)
    }
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  const hasError = !!errors[name]

  const inputElement = (
    <input
      name={name}
      ref={inputRef}
      type={type}
      placeholder={placeholder}
      className={cn(styles.input, {
        [styles.focused]: isFocused,
        [styles.hasError]: hasError,
        [styles.withStartIcon]: !!startIcon,
      })}
      data-state-error={hasError}
      value={isFocused ? (parseValue(value) ?? '') : (displayValue(value) ?? '')}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
    />
  )

  return (
    <>
      {startIcon ? (
        <div
          className={cn(styles.inputWrapper, {
            [styles.hasError]: hasError,
          })}
          data-has-start-icon="true"
        >
          <div
            className={cn(styles.startIcon, {
              [styles.iconError]: hasError,
            })}
          >
            {startIcon}
          </div>
          {inputElement}
        </div>
      ) : (
        inputElement
      )}
      {hasError && (
        <Typography variant="caption" size="l" color="var(--color-error-500)">
          {errors[name]}
        </Typography>
      )}
    </>
  )
}

Input.displayName = 'Form.Input'
export default Input
